import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import {
    FaFolder, FaFolderPlus, FaUpload, FaTrash, FaShare,
    FaEdit, FaHome, FaChevronRight, FaFile, FaFilePdf,
    FaFileImage, FaFileWord, FaFileExcel, FaFilePowerpoint,
    FaFileArchive, FaFileAudio, FaFileVideo, FaFileCode,
    FaTh, FaList, FaSearch, FaTimes, FaArrowRight, FaEye
} from 'react-icons/fa';
import { notify } from '../CustomToast';
import { useFileManager } from '../../hooks/useFileManager';
import { useI18n } from '../../i18n';
import trashIcon from '../../assets/trash.svg';
import '../Projects/DeleteProjectModal.css';
import FileShareModal from './FileShareModal';
import BackButton from '../Common/BackButton';
import { useDashboard } from '../../context/DashboardContext';
import uploadSimpleIcon from '../../assets/icons/upload-simple.svg';
import dotsThreeIcon from '../../assets/icons/dots-three-vertical.svg';
import FileManagerSkeleton from './FileManagerSkeleton';
import '../Projects/ProjectComposer.css';
import './FileManagerSection.css';
import MoveModal from './MoveModal';
import OnlyOfficeViewerModal from './OnlyOfficeViewerModal';
import ImageViewerModal from './ImageViewerModal';
import PdfViewerModal, { isPdfFile } from './PdfViewerModal';

// File type icons mapping
const getFileIcon = (mimeType, extension) => {
    if (mimeType?.startsWith('image/')) return <FaFileImage className="file-icon image" />;
    if (mimeType?.startsWith('video/')) return <FaFileVideo className="file-icon video" />;
    if (mimeType?.startsWith('audio/')) return <FaFileAudio className="file-icon audio" />;
    if (mimeType === 'application/pdf') return <FaFilePdf className="file-icon pdf" />;
    if (mimeType?.includes('word') || extension === 'doc' || extension === 'docx') return <FaFileWord className="file-icon word" />;
    if (mimeType?.includes('excel') || extension === 'xls' || extension === 'xlsx') return <FaFileExcel className="file-icon excel" />;
    if (mimeType?.includes('powerpoint') || extension === 'ppt' || extension === 'pptx') return <FaFilePowerpoint className="file-icon powerpoint" />;
    if (mimeType?.includes('zip') || mimeType?.includes('rar') || mimeType?.includes('7z')) return <FaFileArchive className="file-icon archive" />;
    if (mimeType?.includes('javascript') || mimeType?.includes('json') || mimeType?.includes('html') || mimeType?.includes('css')) return <FaFileCode className="file-icon code" />;
    return <FaFile className="file-icon default" />;
};

// Office file types supported by OnlyOffice
const OFFICE_EXTENSIONS = ['doc','docx','odt','rtf','xls','xlsx','ods','csv','ppt','pptx','odp'];
const isOfficeFile = (file) => {
    const ext = (file.original_filename || file.name || '').split('.').pop()?.toLowerCase();
    return OFFICE_EXTENSIONS.includes(ext);
};

// Image file types for modal viewer
const IMAGE_EXTENSIONS = ['jpg','jpeg','png','gif','svg','webp','bmp','tiff','tif','ico','avif'];
const isImageFile = (file) => {
    const ext = (file.original_filename || file.name || '').split('.').pop()?.toLowerCase();
    return IMAGE_EXTENSIONS.includes(ext);
};

const FileManagerSection = () => {
    const { t } = useI18n();
    const { setActiveView } = useDashboard();
    const {
        rootFolders,
        currentFolder,
        folderContents,
        breadcrumb,
        loading,
        isProcessing,
        uploadProgress,
        navigateToFolder,
        createFolder,
        renameFolder,
        deleteFolder,
        uploadFile,
        deleteFile,
        openFile,
        refetchContents,
        moveFolder,
        moveFile,
    } = useFileManager();

    const [viewMode, setViewMode] = useState('grid');
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [showRename, setShowRename] = useState(false);
    const [renameItem, setRenameItem] = useState(null);
    const [renameName, setRenameName] = useState('');
    const [showDelete, setShowDelete] = useState(false);
    const [deleteItem, setDeleteItem] = useState(null);
    const [showShare, setShowShare] = useState(false);
    const [shareItem, setShareItem] = useState(null);
    const [showMove, setShowMove] = useState(false);
    const [moveItem, setMoveItem] = useState(null);
    const [officeViewerFile, setOfficeViewerFile] = useState(null);
    const [imageViewerFile, setImageViewerFile] = useState(null);
    const [pdfViewerFile, setPdfViewerFile] = useState(null);
    const [contextMenu, setContextMenu] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showUploadDropdown, setShowUploadDropdown] = useState(false);
    const [visibleCount, setVisibleCount] = useState(12);
    const fileInputRef = useRef(null);
    const contentRef = useRef(null);

    const currentUserId = useMemo(() => {
        try {
            const u = JSON.parse(localStorage.getItem('user') || '{}');
            return u?.id || null;
        } catch (e) {
            return null;
        }
    }, []);

    // Get all image files in current folder for navigation
    const allImageFiles = useMemo(() => {
        const files = folderContents?.files || [];
        return files.filter(isImageFile);
    }, [folderContents]);

    // Reset pagination when folder changes
    useEffect(() => {
        setVisibleCount(12);
    }, [currentFolder]);

    // Scroll-based pagination
    const handleContentScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 100) {
            setVisibleCount(prev => prev + 12);
        }
    }, []);

    // Format date helper
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const year = d.getFullYear();
        const hours = d.getHours();
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const h = hours % 12 || 12;
        return `${month}-${day}-${year} ${h}:${minutes} ${ampm}`;
    };

    // Handle folder click
    const handleFolderClick = (folder) => {
        navigateToFolder(folder.id);
    };

    // Handle file click
    const handleFileClick = (file) => {
        if (isImageFile(file)) {
            setImageViewerFile(file);
        } else if (isPdfFile(file)) {
            setPdfViewerFile(file);
        } else if (isOfficeFile(file)) {
            setOfficeViewerFile(file);
        } else {
            openFile(file.id);
        }
    };

    // Handle breadcrumb navigation
    const handleBreadcrumbClick = (index) => {
        if (index === -1) {
            navigateToFolder(null);
        } else {
            navigateToFolder(breadcrumb[index].id);
        }
    };

    // Handle create folder
    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) {
            notify('error', t('pages.file_manager.errors.enter_folder_name'));
            return;
        }
        try {
            await createFolder(newFolderName.trim(), currentFolder?.id || null);
            setNewFolderName('');
            setShowCreateFolder(false);
        } catch (err) {
            // Error handled in hook
        }
    };

    // Handle rename
    const handleRename = async () => {
        if (!renameName.trim()) {
            notify('error', t('pages.file_manager.errors.enter_name'));
            return;
        }
        try {
            if (renameItem.type === 'folder') {
                await renameFolder(renameItem.id, renameName.trim());
            }
            setRenameName('');
            setShowRename(false);
            setRenameItem(null);
        } catch (err) {
            // Error handled in hook
        }
    };

    // Handle delete
    const handleDelete = async () => {
        try {
            if (deleteItem.type === 'folder') {
                await deleteFolder(deleteItem.id);
            } else {
                await deleteFile(deleteItem.id);
            }
            setShowDelete(false);
            setDeleteItem(null);
        } catch (err) {
            // Error handled in hook
        }
    };

    // Handle move
    const handleMoveConfirm = async (itemId, destinationId) => {
        try {
            if (moveItem.type === 'folder') {
                await moveFolder(itemId, destinationId);
            } else {
                await moveFile(itemId, destinationId);
            }
            setShowMove(false);
            setMoveItem(null);
        } catch (err) {
            // Error handled in hook
        }
    };

    // Handle file upload
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            notify('error', t('pages.file_manager.errors.max_size'));
            return;
        }

        if (!currentFolder) {
            notify('error', t('pages.file_manager.errors.select_folder'));
            return;
        }

        try {
            await uploadFile(currentFolder.id, file);
        } catch (err) {
            // Error handled in hook
        }

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle context menu
    const handleContextMenu = (e, item, type) => {
        e.preventDefault();
        const menuWidth = 210;
        const menuHeight = 260;
        const x = Math.max(10, e.clientX + menuWidth > window.innerWidth ? window.innerWidth - menuWidth - 10 : e.clientX);
        const y = Math.max(10, e.clientY + menuHeight > window.innerHeight ? window.innerHeight - menuHeight - 10 : e.clientY);
        setContextMenu({ x, y, item: { ...item, type } });
    };

    // Close context menu
    const closeContextMenu = () => {
        setContextMenu(null);
        setShowUploadDropdown(false);
    };

    // Check if user is owner
    const isOwner = (item) => {
        return item?.owner_id === currentUserId || item?.owner?.id === currentUserId;
    };

    // Render items in root view
    const renderRootView = () => {
        const ownedFolders = rootFolders.owned || [];
        const sharedFolders = rootFolders.shared || [];
        const sharedFiles = rootFolders.sharedFiles || [];
        const allFolders = [
            ...ownedFolders.map(f => ({ ...f, _section: 'owned' })),
            ...sharedFolders.map(f => ({ ...f, _section: 'shared' })),
        ];
        const visible = allFolders.slice(0, visibleCount);

        if (allFolders.length === 0 && sharedFiles.length === 0) {
            return (
                <div className="empty-state">
                    <FaFolder className="empty-icon" />
                    <p>{t('pages.file_manager.no_folders')}</p>
                    <button className="btn-primary" onClick={() => setShowCreateFolder(true)}>
                        <FaFolderPlus /> {t(currentFolder ? 'pages.file_manager.create_sub_folder' : 'pages.file_manager.create_folder')}
                    </button>
                </div>
            );
        }

        return (
            <>
                {allFolders.length > 0 && (
                    <div className="fm-items-grid">
                        {visible.map(folder => (
                            <div
                                key={folder.id}
                                className="fm-item"
                                onClick={() => handleFolderClick(folder)}
                                onContextMenu={(e) => handleContextMenu(e, folder, 'folder')}
                            >
                                <FaFolder className="fm-item-icon folder" />
                                <div className="fm-item-info">
                                    <span className="fm-item-name">{folder.name}</span>
                                    <span className="fm-item-date">{formatDate(folder.created_at)}</span>
                                </div>
                                <button
                                    className="fm-item-dots"
                                    onClick={(e) => { e.stopPropagation(); handleContextMenu(e, folder, 'folder'); }}
                                >
                                    <img src={dotsThreeIcon} alt="More" width="32" height="32" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {sharedFiles.length > 0 && (
                    <>
                        <p className="fm-section-label">Shared with me</p>
                        <div className="fm-items-grid">
                            {sharedFiles.map(item => (
                                <div
                                    key={`shared-file-${item.id}`}
                                    className="fm-item"
                                    onClick={() => handleFileClick(item)}
                                    onContextMenu={(e) => handleContextMenu(e, item, 'file')}
                                >
                                    <span className="fm-item-icon file">{getFileIcon(item.mime_type, item.extension)}</span>
                                    <div className="fm-item-info">
                                        <span className="fm-item-name">{item.original_filename}</span>
                                        <span className="fm-item-date">{formatDate(item.created_at)}</span>
                                    </div>
                                    {isOfficeFile(item) && (
                                        <button
                                            id={`oo-view-btn-shared-${item.id}`}
                                            className="fm-view-btn"
                                            title="View in OnlyOffice"
                                            onClick={(e) => { e.stopPropagation(); setOfficeViewerFile(item); }}
                                        >
                                            <FaEye />
                                        </button>
                                    )}
                                    {isImageFile(item) && (
                                        <button
                                            id={`img-view-btn-shared-${item.id}`}
                                            className="fm-view-btn"
                                            title="View image"
                                            onClick={(e) => { e.stopPropagation(); setImageViewerFile(item); }}
                                        >
                                            <FaEye />
                                        </button>
                                    )}
                                    {isPdfFile(item) && (
                                        <button
                                            id={`pdf-view-btn-shared-${item.id}`}
                                            className="fm-view-btn"
                                            title="View PDF"
                                            onClick={(e) => { e.stopPropagation(); setPdfViewerFile(item); }}
                                        >
                                            <FaEye />
                                        </button>
                                    )}
                                    <button
                                        className="fm-item-dots"
                                        onClick={(e) => { e.stopPropagation(); handleContextMenu(e, item, 'file'); }}
                                    >
                                        <img src={dotsThreeIcon} alt="More" width="32" height="32" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </>
        );
    };

    // Render folder contents
    const renderFolderContents = () => {
        const subfolders = folderContents.subfolders || [];
        const files = folderContents.files || [];

        if (subfolders.length === 0 && files.length === 0) {
            return (
                <div className="empty-state">
                    <FaFolder className="empty-icon" />
                    <p>{t('pages.file_manager.empty_folder')}</p>
                </div>
            );
        }

        const allItems = [
            ...subfolders.map(f => ({ ...f, _type: 'folder' })),
            ...files.map(f => ({ ...f, _type: 'file' })),
        ];
        const visible = allItems.slice(0, visibleCount);

        return (
            <div className="fm-items-grid">
                {visible.map(item => (
                    <div
                        key={`${item._type}-${item.id}`}
                        className="fm-item"
                        onClick={() => item._type === 'folder' ? handleFolderClick(item) : handleFileClick(item)}
                        onContextMenu={(e) => handleContextMenu(e, item, item._type)}
                    >
                        {item._type === 'folder'
                            ? <FaFolder className="fm-item-icon folder" />
                            : <span className="fm-item-icon file">{getFileIcon(item.mime_type, item.extension)}</span>
                        }
                        <div className="fm-item-info">
                            <span className="fm-item-name">{item._type === 'folder' ? item.name : item.original_filename}</span>
                            <span className="fm-item-date">{formatDate(item.created_at)}</span>
                        </div>
                        {item._type === 'file' && isOfficeFile(item) && (
                            <button
                                id={`oo-view-btn-${item.id}`}
                                className="fm-view-btn"
                                title="View in OnlyOffice"
                                onClick={(e) => { e.stopPropagation(); setOfficeViewerFile(item); }}
                            >
                                <FaEye />
                            </button>
                        )}
                        {item._type === 'file' && isImageFile(item) && (
                            <button
                                id={`img-view-btn-${item.id}`}
                                className="fm-view-btn"
                                title="View image"
                                onClick={(e) => { e.stopPropagation(); setImageViewerFile(item); }}
                            >
                                <FaEye />
                            </button>
                        )}
                        {item._type === 'file' && isPdfFile(item) && (
                            <button
                                id={`pdf-view-btn-${item.id}`}
                                className="fm-view-btn"
                                title="View PDF"
                                onClick={(e) => { e.stopPropagation(); setPdfViewerFile(item); }}
                            >
                                <FaEye />
                            </button>
                        )}
                        <button
                            className="fm-item-dots"
                            onClick={(e) => { e.stopPropagation(); handleContextMenu(e, item, item._type); }}
                        >
                            <img src={dotsThreeIcon} alt="More" width="32" height="32" />
                        </button>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="file-manager-section" onClick={closeContextMenu}>
            {/* Header */}
            <div className="dashboard-section-header fm-header-row">
                <h1 className="dashboard-section-title">
                    <BackButton
                        onClick={() => setActiveView('dashboard')}
                        ariaLabel={t('common.navigation.back_to_dashboard')}
                    />
                    {t('pages.file_manager.title')}
                </h1>
                <div className="fm-upload-wrapper" onClick={(e) => e.stopPropagation()}>
                    <button
                        className="fm-upload-btn"
                        onClick={() => setShowUploadDropdown(!showUploadDropdown)}
                        disabled={isProcessing}
                    >
                        <img src={uploadSimpleIcon} alt="" width="20" height="20" />
                        {t('pages.file_manager.upload_files')}
                    </button>
                    {showUploadDropdown && (
                        <div className="fm-upload-dropdown">
                            {currentFolder && (
                                <button
                                    className="fm-dropdown-item"
                                    onClick={() => {
                                        fileInputRef.current?.click();
                                        setShowUploadDropdown(false);
                                    }}
                                >
                                    <FaUpload className="fm-dropdown-icon" />
                                    {t('pages.file_manager.upload_file')}
                                </button>
                            )}
                            <button
                                className="fm-dropdown-item"
                                onClick={() => {
                                    setShowCreateFolder(true);
                                    setShowUploadDropdown(false);
                                }}
                            >
                                <FaFolderPlus className="fm-dropdown-icon" />
                                {t(currentFolder ? 'pages.file_manager.create_sub_folder' : 'pages.file_manager.create_folder')}
                            </button>
                        </div>
                    )}
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                    />
                </div>
            </div>
            {/* Breadcrumbs */}
            <div className="fm-breadcrumb">
                <span
                    className="fm-breadcrumb-item"
                    onClick={() => handleBreadcrumbClick(-1)}
                >
                    {t('pages.file_manager.my_folders')}
                </span>
                {breadcrumb.map((item, index) => (
                    <React.Fragment key={item.id}>
                        <span className="fm-breadcrumb-sep">&gt;</span>
                        <span
                            className="fm-breadcrumb-item"
                            onClick={() => handleBreadcrumbClick(index)}
                        >
                            {item.name}
                        </span>
                    </React.Fragment>
                ))}
            </div>
            {/* Upload Progress */}
            {uploadProgress !== null && (
                <div className="upload-progress">
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${uploadProgress}%` }}
                        />
                    </div>
                    <span>{t('pages.file_manager.upload_progress', { progress: uploadProgress })}</span>
                </div>
            )}

            {/* Content */}
            <div className="file-manager-content" ref={contentRef} onScroll={handleContentScroll}>
                {loading ? (
                    <FileManagerSkeleton count={9} />
                ) : currentFolder ? (
                    renderFolderContents()
                ) : (
                    renderRootView()
                )}
            </div>

            {/* Context Menu */}
            {contextMenu && (
                <div
                    className="fm-upload-dropdown fm-context-menu"
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {contextMenu.item.type === 'folder' && (
                        <button className="fm-dropdown-item" onClick={() => {
                            handleFolderClick(contextMenu.item);
                            closeContextMenu();
                        }}>
                            <FaFolder className="fm-dropdown-icon" /> {t('pages.file_manager.open')}
                        </button>
                    )}
                    {contextMenu.item.type === 'file' && (
                        <button className="fm-dropdown-item" onClick={() => {
                            handleFileClick(contextMenu.item);
                            closeContextMenu();
                        }}>
                            <FaFile className="fm-dropdown-icon" /> {t('pages.file_manager.open')}
                        </button>
                    )}
                    {isOwner(contextMenu.item) && contextMenu.item.type === 'folder' && (
                        <button className="fm-dropdown-item" onClick={() => {
                            setRenameItem(contextMenu.item);
                            setRenameName(contextMenu.item.name);
                            setShowRename(true);
                            closeContextMenu();
                        }}>
                            <FaEdit className="fm-dropdown-icon" /> {t('pages.file_manager.rename')}
                        </button>
                    )}
                    {isOwner(contextMenu.item) && (
                        <>
                            <button className="fm-dropdown-item" onClick={() => {
                                setShareItem(contextMenu.item);
                                setShowShare(true);
                                closeContextMenu();
                            }}>
                                <FaShare className="fm-dropdown-icon" /> {t('pages.file_manager.share')}
                            </button>
                            <button className="fm-dropdown-item" onClick={() => {
                                setMoveItem(contextMenu.item);
                                setShowMove(true);
                                closeContextMenu();
                            }}>
                                <FaArrowRight className="fm-dropdown-icon" /> {t('pages.file_manager.move')}
                            </button>
                            <button className="fm-dropdown-item fm-dropdown-danger" onClick={() => {
                                setDeleteItem(contextMenu.item);
                                setShowDelete(true);
                                closeContextMenu();
                            }}>
                                <FaTrash className="fm-dropdown-icon fm-dropdown-danger-icon" /> {t('pages.file_manager.delete')}
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* Create Folder Modal */}
            {showCreateFolder && (
                <div className="project-composer-overlay" onClick={(e) => { if (e.target === e.currentTarget && !isProcessing) setShowCreateFolder(false); }}>
                    <div className="project-composer-modal" style={{ width: '500px', maxWidth: 'calc(100% - 32px)', margin: '0 16px' }}>
                        <div className="project-composer-header">
                            <h2 className="project-composer-title">{t('pages.file_manager.create_new_folder')}</h2>
                            <button
                                className="project-composer-close"
                                onClick={() => setShowCreateFolder(false)}
                                disabled={isProcessing}
                                aria-label="Close"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="project-composer-body">
                            <div className="project-form-group">
                                <label className="project-form-label">{t('pages.file_manager.folder_name')}</label>
                                <input
                                    type="text"
                                    className="project-form-input"
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                    placeholder={t('pages.file_manager.folder_placeholder')}
                                    autoFocus
                                    onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                                    disabled={isProcessing}
                                />
                            </div>
                        </div>
                        <div className="project-composer-footer">
                            <button
                                type="button"
                                className="project-composer-btn project-composer-btn-secondary"
                                onClick={() => setShowCreateFolder(false)}
                                disabled={isProcessing}
                            >
                                {t('common.actions.cancel')}
                            </button>
                            <button
                                type="button"
                                className="project-composer-btn project-composer-btn-primary"
                                onClick={handleCreateFolder}
                                disabled={isProcessing}
                            >
                                {isProcessing ? t('common.status.submitting') : t('common.actions.create')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rename Modal */}
            {showRename && renameItem && (
                <div className="project-composer-overlay" onClick={(e) => { if (e.target === e.currentTarget && !isProcessing) { setShowRename(false); setRenameItem(null); } }}>
                    <div className="project-composer-modal" style={{ width: '500px', maxWidth: 'calc(100% - 32px)', margin: '0 16px' }}>
                        <div className="project-composer-header">
                            <h2 className="project-composer-title">
                                {t('pages.file_manager.rename_item', { type: renameItem?.type === 'folder' ? t('pages.file_manager.folder') : t('pages.file_manager.file') })}
                            </h2>
                            <button
                                className="project-composer-close"
                                onClick={() => { setShowRename(false); setRenameItem(null); }}
                                disabled={isProcessing}
                                aria-label="Close"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="project-composer-body">
                            <div className="project-form-group">
                                <label className="project-form-label">{t('pages.file_manager.new_name')}</label>
                                <input
                                    type="text"
                                    className="project-form-input"
                                    value={renameName}
                                    onChange={(e) => setRenameName(e.target.value)}
                                    placeholder={t('pages.file_manager.new_name')}
                                    autoFocus
                                    onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                                    disabled={isProcessing}
                                />
                            </div>
                        </div>
                        <div className="project-composer-footer">
                            <button
                                type="button"
                                className="project-composer-btn project-composer-btn-secondary"
                                onClick={() => { setShowRename(false); setRenameItem(null); }}
                                disabled={isProcessing}
                            >
                                {t('common.actions.cancel')}
                            </button>
                            <button
                                type="button"
                                className="project-composer-btn project-composer-btn-primary"
                                onClick={handleRename}
                                disabled={isProcessing}
                            >
                                {isProcessing ? t('common.status.saving') : t('pages.file_manager.rename')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDelete && deleteItem && (
                <div className="delete-project-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !isProcessing) { setShowDelete(false); setDeleteItem(null); } }}>
                    <div className="delete-project-modal">
                        <button
                            className="delete-project-modal-close"
                            onClick={() => { setShowDelete(false); setDeleteItem(null); }}
                            disabled={isProcessing}
                            aria-label="Close"
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>

                        <div className="delete-project-modal-header">
                            <div className="delete-project-modal-header-left">
                                <img src={trashIcon} alt="Delete" className="delete-project-modal-small-icon" />
                                <h2 className="delete-project-modal-title">
                                    {t('pages.file_manager.delete_item', { type: deleteItem?.type === 'folder' ? t('pages.file_manager.folder') : t('pages.file_manager.file') })}
                                </h2>
                            </div>
                        </div>

                        <p className="delete-project-modal-description">
                            {deleteItem?.type === 'folder'
                                ? t('pages.file_manager.confirm_delete_folder', { name: deleteItem?.name })
                                : t('pages.file_manager.confirm_delete_file', { name: deleteItem?.original_filename || deleteItem?.name })
                            }
                        </p>

                        <div className="delete-project-modal-actions">
                            <button
                                className="delete-project-modal-cancel"
                                onClick={() => { setShowDelete(false); setDeleteItem(null); }}
                                disabled={isProcessing}
                            >
                                {t('common.actions.cancel')}
                            </button>
                            <button
                                className="delete-project-modal-delete"
                                onClick={handleDelete}
                                disabled={isProcessing}
                            >
                                {isProcessing ? t('common.status.deleting') : t('common.actions.delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Share Modal */}
            <FileShareModal
                isOpen={showShare}
                onClose={() => { setShowShare(false); setShareItem(null); }}
                item={shareItem}
                onShareUpdated={() => refetchContents()}
            />

            {/* Move Modal */}
            <MoveModal
                isOpen={showMove}
                onClose={() => { setShowMove(false); setMoveItem(null); }}
                onMove={handleMoveConfirm}
                item={moveItem}
                currentFolderId={currentFolder?.id || null}
            />

            {/* OnlyOffice Viewer Modal */}
            {officeViewerFile && (
                <OnlyOfficeViewerModal
                    file={officeViewerFile}
                    onClose={(wasModified) => {
                        setOfficeViewerFile(null);
                        // If file was edited, wait for OnlyOffice callback to complete then refresh
                        if (wasModified) setTimeout(() => refetchContents(), 3500);
                    }}
                />
            )}

            {/* Image Viewer Modal */}
            {imageViewerFile && (
                <ImageViewerModal
                    file={imageViewerFile}
                    allImages={allImageFiles}
                    onClose={() => setImageViewerFile(null)}
                />
            )}

            {/* PDF Viewer Modal */}
            {pdfViewerFile && (
                <PdfViewerModal
                    file={pdfViewerFile}
                    onClose={() => setPdfViewerFile(null)}
                />
            )}
        </div>
    );
};

export default FileManagerSection;
